import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar as CalUI } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { fmtLocalDate, parseLocalDate } from "@/lib/utils";
import { humanizeAxiosError } from "@/lib/api";

const KIND_LABEL = { holiday: "Resmi Tatil", meeting: "Toplantı", training: "Eğitim", other: "Diğer", birthday: "Doğum Günü", leave: "İzin" };
const KIND_COLOR = { holiday: "destructive", meeting: "default", training: "secondary", other: "outline", birthday: "warning", leave: "secondary" };
const EMPTY_FORM = { title: "", date: "", kind: "holiday", notes: "", is_half_day: false };

export default function CalendarPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // event being edited
  const [confirmDelete, setConfirmDelete] = useState(null); // event pending delete
  const [selected, setSelected] = useState(new Date());

  // Only admin, HR, GM can add/update/cancel. Managers and staff cannot.
  const canManage = ["admin", "hr", "gm"].includes(user?.role);

  const load = () => api.get("/calendar").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM, date: fmtLocalDate(selected) }); setOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title || "",
      date: (ev.date || "").slice(0, 10),
      kind: ev.kind || "other",
      notes: ev.notes || "",
      is_half_day: !!ev.is_half_day,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title || !form.date) return toast.error("Başlık ve tarih zorunlu");
    try {
      if (editing) {
        await api.patch(`/calendar/${editing.id}`, form);
        toast.success("Etkinlik güncellendi");
      } else {
        await api.post("/calendar", form);
        toast.success("Etkinlik eklendi");
      }
      setForm(EMPTY_FORM); setEditing(null); setOpen(false); load();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/calendar/${confirmDelete.id}`);
      toast.success("Etkinlik iptal edildi");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    }
  };

  const selectedIso = fmtLocalDate(selected);
  const dayEvents = events.filter((e) => (e.date || "").slice(0, 10) === selectedIso);
  const eventDates = events.map((e) => parseLocalDate(e.date)).filter(Boolean);

  // Month sidebar — events for the month of the selected date, sorted.
  const monthEvents = React.useMemo(() => {
    const y = selected.getFullYear();
    const m = selected.getMonth();
    return events
      .filter((e) => {
        const d = parseLocalDate(e.date);
        return d && d.getFullYear() === y && d.getMonth() === m;
      })
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [selected, events]);
  const monthLabel = selected.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

  // Auto-generated events (birthdays, leaves) are not editable — they have prefixed IDs.
  const isEditable = (ev) => canManage && ev.id && !String(ev.id).startsWith("bd-") && !String(ev.id).startsWith("lv-");

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Planlama</div>
          <h1 className="font-display text-3xl font-bold mt-1">Takvim</h1>
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="new-event-btn"><Plus size={16} className="mr-1" />Yeni Etkinlik</Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <CalUI mode="single" selected={selected} onSelect={setSelected} modifiers={{ hasEvent: eventDates }} modifiersStyles={{ hasEvent: { backgroundColor: "#C05A42", color: "white" } }} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{selectedIso}</div>
            <div className="space-y-2">
              {dayEvents.length === 0 && <div className="text-sm text-muted-foreground">Etkinlik yok</div>}
              {dayEvents.map((e) => (
                <div key={e.id} className="border-l-2 border-accent pl-3 py-1 group" data-testid={`event-item-${e.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={KIND_COLOR[e.kind] || "outline"}>{KIND_LABEL[e.kind] || e.kind}</Badge>
                      {e.is_half_day && <Badge variant="warning" className="text-[10px]">0.5 GÜN</Badge>}
                    </div>
                    {isEditable(e) && (
                      <div className="flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(e)} data-testid={`event-edit-${e.id}`} title="Düzenle">
                          <PencilSimple size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setConfirmDelete(e)} data-testid={`event-delete-${e.id}`} title="İptal et">
                          <Trash size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="font-semibold text-sm mt-1">{e.title}</div>
                  {e.notes && <div className="text-xs text-muted-foreground">{e.notes}</div>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="month-sidebar">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bu Ay</div>
              <div className="font-display text-xl font-bold capitalize">{monthLabel}</div>
            </div>
            <Badge variant="secondary">{monthEvents.length} etkinlik</Badge>
          </div>
          {monthEvents.length === 0 ? (
            <div className="text-sm text-muted-foreground">Bu ay için etkinlik yok.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {monthEvents.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => { const d = parseLocalDate(e.date); if (d) setSelected(d); }}
                  className="text-left rounded-md border border-border hover:border-primary p-2 transition-colors group"
                  data-testid={`month-event-${e.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[11px] font-mono text-muted-foreground">{(e.date || "").slice(5, 10)}</span>
                    <Badge variant={KIND_COLOR[e.kind] || "outline"} className="text-[9px]">{KIND_LABEL[e.kind] || e.kind}</Badge>
                  </div>
                  <div className="text-sm font-medium truncate group-hover:text-primary">{e.title}</div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY_FORM); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Etkinliği Düzenle" : "Yeni Etkinlik"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="ev-title" /></div>
            <div><Label>Tarih</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} data-testid="ev-date" /></div>
            <div>
              <Label>Tip</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({...form, kind: v})}>
                <SelectTrigger data-testid="ev-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="holiday">Resmi Tatil</SelectItem>
                  <SelectItem value="meeting">Toplantı</SelectItem>
                  <SelectItem value="training">Eğitim</SelectItem>
                  <SelectItem value="other">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notlar</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
            {form.kind === "holiday" && (
              <div className="flex items-center gap-2 bg-muted/50 rounded-md p-3">
                <Checkbox id="ev-half-day" checked={form.is_half_day} onCheckedChange={(v) => setForm({...form, is_half_day: !!v})} data-testid="ev-half-day" />
                <Label htmlFor="ev-half-day" className="cursor-pointer text-sm">Yarım Gün Tatil (izin hesabında 0.5 gün olarak sayılır)</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); setForm(EMPTY_FORM); }}>Vazgeç</Button>
            <Button onClick={submit} data-testid="ev-submit">{editing ? "Güncelle" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Etkinliği iptal et?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{confirmDelete?.title}</span> ({(confirmDelete?.date || "").slice(0,10)}) takvimden kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="event-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="event-delete-confirm">Evet, iptal et</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
